import React, { useEffect, useMemo, useState, useRef } from "react";
import { Search, Plus, Download, Upload, ClipboardList, CheckCircle2, XCircle, Trash2, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, downloadFile, wibDateISO, wibDaysAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const ST = { Counting: "bg-amber-50 text-amber-700 border-amber-200", Applied: "bg-green-50 text-green-700 border-green-200", Cancelled: "bg-slate-100 text-slate-400 border-slate-200" };
const STLBL = { Counting: "Berjalan", Applied: "Selesai", Cancelled: "Dibatalkan" };

export default function Opname() {
  const { can } = useAuth();
  const canWrite = can("warehouse_admin");
  const [q, setQ] = useState(""); const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1); const limit = 10;
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);
  const params = useMemo(() => ({ q: debounced || undefined, status: status !== "all" ? status : undefined, page, limit }), [debounced, status, page]);
  const load = () => { setLoading(true); api.get("/opname", { params }).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat")).finally(() => setLoading(false)); };
  useEffect(load, [params]);
  const start0 = (data.page - 1) * limit;

  return (
    <div data-testid="opname-page">
      <PageHeader title="Stok Opname" subtitle="Hitung fisik tanpa stop jualan — selisih dinetralkan dari penjualan">
        {canWrite && <Button onClick={() => setCreateOpen(true)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="opname-create-btn"><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
      </PageHeader>
      <Panel className="p-0">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-taupe/15">
          <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no / nama sesi..." className="pl-8 h-9" data-testid="opname-search" /></div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-40" data-testid="opname-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Counting">Berjalan</SelectItem><SelectItem value="Applied">Selesai</SelectItem><SelectItem value="Cancelled">Dibatalkan</SelectItem></SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead className="w-12">No</TableHead><TableHead>No. Opname</TableHead><TableHead>Nama / Periode</TableHead><TableHead>Gudang</TableHead><TableHead className="text-center">Progres</TableHead><TableHead>Status</TableHead><TableHead>Mulai</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-wmuted">Memuat...</TableCell></TableRow>
              : data.items.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-wmuted">Belum ada sesi opname</TableCell></TableRow>
                : data.items.map((r, i) => (
                  <TableRow key={r.id} data-testid={`opname-row-${r.id}`}>
                    <TableCell className="text-wmuted">{start0 + i + 1}</TableCell>
                    <TableCell className="font-medium text-cocoa">{r.number}</TableCell>
                    <TableCell>{r.name}{r.period_label ? <div className="text-xs text-wmuted">{r.period_label}</div> : null}</TableCell>
                    <TableCell className="text-sm">{(r.warehouse_names || []).join(", ") || "Semua"}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-0.5" data-testid={`opname-progress-${r.id}`}>
                        <span className="text-sm font-medium text-cocoa">{num(r.counted_lines)}{r.total_lines ? ` / ${num(r.total_lines)}` : ""}<span className="text-[11px] text-wmuted ml-1">varian</span></span>
                        {r.total_lines > 0 && <div className="w-20 h-1.5 rounded-full bg-taupe/20 overflow-hidden"><div className="h-full bg-cocoa transition-all" style={{ width: `${Math.min(100, Math.round((r.counted_lines / r.total_lines) * 100))}%` }} /></div>}
                      </div>
                    </TableCell>
                    <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${ST[r.status]}`}>{STLBL[r.status]}</span></TableCell>
                    <TableCell className="text-sm">{(r.started_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setSession(r)} data-testid={`opname-open-${r.id}`}>{r.status === "Counting" ? "Hitung" : "Detail"}</Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm">
          <span className="text-wmuted">Total {data.total} sesi</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="opname-prev">Previous</Button>
            <span className="px-3 text-wmuted">Hal {data.page} / {data.pages}</span>
            <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="opname-next">Next</Button>
          </div>
        </div>
      </Panel>
      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onSaved={(s) => { setCreateOpen(false); load(); setSession(s); }} />}
      {session && <CountDialog sess={session} canWrite={canWrite} onClose={() => { setSession(null); load(); }} />}
    </div>
  );
}

function CreateDialog({ onClose, onSaved }) {
  const [name, setName] = useState(""); const [period, setPeriod] = useState(""); const [note, setNote] = useState("");
  const [whs, setWhs] = useState([]); const [sel, setSel] = useState([]); const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/warehouses").then((r) => setWhs(r.data || [])).catch(() => {}); }, []);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const save = async () => {
    if (!name.trim()) return toast.error("Nama sesi wajib diisi");
    setSaving(true);
    try { const r = await api.post("/opname", { name, period_label: period, warehouse_ids: sel, note }); toast.success("Sesi opname dibuat"); onSaved(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-lg" data-testid="opname-create-dialog">
        <DialogHeader><DialogTitle>Buat Sesi Stok Opname</DialogTitle><DialogDescription>Jualan tetap jalan. Selisih otomatis dinetralkan dari transaksi selama opname.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nama Sesi *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Opname Akhir Bulan" data-testid="opname-name" autoFocus /></div>
          <div><Label>Periode (opsional)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Agustus 2026" data-testid="opname-period" /></div>
          <div><Label>Gudang (kosongkan = semua)</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1 max-h-40 overflow-y-auto">
              {whs.map((w) => (
                <label key={w.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer text-sm ${sel.includes(w.id) ? "border-cocoa bg-cocoa/5" : "border-taupe/25"}`} data-testid={`opname-wh-${w.id}`}>
                  <input type="checkbox" checked={sel.includes(w.id)} onChange={() => toggle(w.id)} />{w.name}
                </label>
              ))}
            </div>
          </div>
          <div><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="opname-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="opname-save-btn">{saving ? "..." : "Mulai Opname"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CountDialog({ sess, canWrite, onClose }) {
  const [detail, setDetail] = useState(null);
  const [pq, setPq] = useState(""); const [results, setResults] = useState([]); const [searching, setSearching] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [showRecon, setShowRecon] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const active = sess.status === "Counting";
  const refresh = () => api.get(`/opname/${sess.id}`).then((r) => setDetail(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => { if (!pq || pq.length < 2) { setResults([]); return; } setSearching(true); api.get(`/opname/${sess.id}/search`, { params: { q: pq } }).then((r) => setResults(r.data.results || [])).catch(() => {}).finally(() => setSearching(false)); }, 350);
    return () => clearTimeout(t);
  }, [pq]);

  const onProductSaved = () => { refresh(); setActiveProduct(null); setPq(""); setResults([]); };
  const delLine = async (lid) => { await api.delete(`/opname/${sess.id}/line/${lid}`); refresh(); };
  const finalize = async () => {
    if (!window.confirm("Finalisasi opname? Selisih akan diterapkan ke stok. Varian yang belum dihitung tidak diubah.")) return;
    try { const r = await api.post(`/opname/${sess.id}/apply`); toast.success(`Selesai. ${r.data.adjusted_lines} penyesuaian diterapkan.`); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async () => { if (!window.confirm("Batalkan sesi ini?")) return; try { await api.post(`/opname/${sess.id}/cancel`); toast.success("Dibatalkan"); onClose(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doExport = () => downloadFile(`/opname/${sess.id}/export`, {}, `opname_${sess.number}.xlsx`).catch(() => toast.error("Gagal export"));
  const dlTemplate = () => downloadFile(`/opname/${sess.id}/count-template`, {}, `template_hitung_${sess.number}.xlsx`).catch(() => toast.error("Gagal unduh template"));
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/opname/${sess.id}/count-import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { saved, skipped, invalid } = r.data;
      toast.success(`Import selesai: ${saved} tersimpan, ${skipped} dilewati${invalid ? `, ${invalid} tidak valid` : ""}`);
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal import"); }
    finally { setImporting(false); }
  };
  const lines = detail?.lines || [];

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="opname-count-dialog">
        <DialogHeader>
          <DialogTitle>{sess.number} — {sess.name} {active ? "" : `(${STLBL[sess.status]})`}</DialogTitle>
          <DialogDescription>Cari varian & isi qty fisik. Selisih = fisik − stok sistem saat dihitung (bebas efek penjualan).</DialogDescription>
        </DialogHeader>

        {detail?.summary && detail.summary.count > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-1" data-testid="opname-summary">
            <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2"><div className="text-[11px] text-wmuted">Dihitung</div><div className="text-lg font-bold text-cocoa">{num(detail.summary.count)}</div></div>
            <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2"><div className="text-[11px] text-wmuted">Selisih (+/−)</div><div className="text-lg font-bold text-cocoa"><span className="text-blue-600">+{num(detail.summary.total_plus)}</span> / <span className="text-red-600">{num(detail.summary.total_minus)}</span></div></div>
            <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2"><div className="text-[11px] text-wmuted">Estimasi Kehilangan</div><div className="text-lg font-bold text-red-600">Rp{num(detail.summary.loss_value)}</div></div>
            <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2"><div className="text-[11px] text-wmuted">Nilai Net</div><div className={`text-lg font-bold ${detail.summary.net_value < 0 ? "text-red-600" : "text-green-600"}`}>Rp{num(detail.summary.net_value)}</div></div>
          </div>
        )}

        {active && canWrite && (
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk untuk dihitung (min 2 huruf)..." className="pl-8" data-testid="opname-item-search" />
            {(results.length > 0 || searching) && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-taupe/25 rounded-lg shadow-lg" data-testid="opname-search-results">
                {searching && <div className="px-3 py-2 text-sm text-wmuted">Mencari...</div>}
                {!searching && results.length === 0 && <div className="px-3 py-2 text-sm text-wmuted">Tidak ada produk</div>}
                {results.map((p) => (
                  <button key={p.product_id} type="button" onClick={() => setActiveProduct(p)}
                    className="w-full text-left flex items-center gap-2 px-3 py-2.5 hover:bg-taupe/10 text-sm border-b border-taupe/5" data-testid={`opname-product-${p.product_id}`}>
                    <div className="flex-1">
                      <div className="font-medium text-cocoa">{p.product_name}</div>
                      <div className="text-xs text-wmuted">{p.variant_count} varian{p.counted_variants ? ` · ${p.counted_variants} sudah dihitung` : ""}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-wmuted" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="border border-taupe/20 rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Varian</TableHead><TableHead>Gudang</TableHead><TableHead className="text-center">Sistem</TableHead><TableHead className="text-center">Keep</TableHead><TableHead className="text-center">Fisik Seharusnya</TableHead><TableHead className="text-center">Mutasi</TableHead><TableHead className="text-center">Fisik</TableHead><TableHead className="text-center">Selisih</TableHead>{active && <TableHead></TableHead>}</TableRow></TableHeader>
            <TableBody>
              {lines.length === 0 ? <TableRow><TableCell colSpan={active ? 9 : 8} className="text-center py-8 text-wmuted">Belum ada yang dihitung</TableCell></TableRow>
                : lines.map((l) => (
                  <TableRow key={l.id} data-testid={`opname-line-${l.id}`} className={l.keep_at_count > 0 ? "bg-amber-50/70 hover:bg-amber-50" : ""}>
                    <TableCell className="text-sm">{l.product_name} <span className="text-wmuted">{l.color} {l.size}</span></TableCell>
                    <TableCell className="text-sm">{l.warehouse_name}</TableCell>
                    <TableCell className="text-center">{num(l.system_at_count)}</TableCell>
                    <TableCell className="text-center">{l.keep_at_count ? <span className="text-amber-600 font-medium">{num(l.keep_at_count)}</span> : <span className="text-wmuted">0</span>}</TableCell>
                    <TableCell className="text-center font-medium text-cocoa">{num(l.expected_physical ?? l.system_at_count)}</TableCell>
                    <TableCell className="text-center text-wmuted">{l.mutation_since_start > 0 ? "+" : ""}{num(l.mutation_since_start)}</TableCell>
                    <TableCell className="text-center font-semibold">{num(l.counted_qty)}</TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className={l.variance === 0 ? "border-green-300 text-green-700" : l.variance < 0 ? "border-red-300 text-red-600" : "border-blue-300 text-blue-600"}>{l.variance > 0 ? "+" : ""}{num(l.variance)}</Badge></TableCell>
                    {active && <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-status-reject" onClick={() => delLine(l.id)} data-testid={`opname-delline-${l.id}`}><X className="w-4 h-4" /></Button></TableCell>}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImportFile} data-testid="opname-import-input" />
          {active && canWrite && <Button variant="outline" onClick={dlTemplate} data-testid="opname-template-btn"><Download className="w-4 h-4 mr-1.5" />Template Hitung</Button>}
          {active && canWrite && <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="opname-import-btn"><Upload className="w-4 h-4 mr-1.5" />{importing ? "Mengimpor..." : "Import Excel"}</Button>}
          <Button variant="outline" onClick={doExport} data-testid="opname-export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
          <Button variant="outline" onClick={() => setShowRecon(true)} data-testid="opname-reconcile-btn"><ClipboardList className="w-4 h-4 mr-1.5" />Export Rekonsiliasi</Button>
          {active && canWrite && <Button variant="outline" onClick={cancel} className="border-status-reject text-status-reject" data-testid="opname-cancel-btn"><XCircle className="w-4 h-4 mr-1.5" />Batalkan</Button>}
          {active && canWrite && <Button onClick={finalize} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="opname-finalize-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" />Finalisasi</Button>}
          <Button variant="outline" onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {activeProduct && <ProductCountDialog sid={sess.id} product={activeProduct} onClose={() => setActiveProduct(null)} onSaved={onProductSaved} />}
    {showRecon && <ReconcileDialog sess={sess} onClose={() => setShowRecon(false)} />}
    </>
  );
}

function ReconcileDialog({ sess, onClose }) {
  const [start, setStart] = useState((sess.started_at || "").slice(0, 10) || wibDaysAgo(30));
  const [end, setEnd] = useState(wibDateISO());
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    if (!start || !end) return toast.error("Isi tanggal dari & sampai");
    if (start > end) return toast.error("Tanggal 'dari' tidak boleh setelah 'sampai'");
    setBusy(true);
    try { await downloadFile(`/opname/${sess.id}/reconcile-export`, { start, end }, `rekonsiliasi_${sess.number}_${start}_${end}.xlsx`); toast.success("Rekonsiliasi diunduh"); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal export rekonsiliasi"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-md" data-testid="opname-reconcile-dialog">
        <DialogHeader>
          <DialogTitle>Export Rekonsiliasi Stok</DialogTitle>
          <DialogDescription>Ringkasan per varian dari histori: Stok Awal, Penerimaan, Retur, Sales (Online/Reseller & Toko), Penyesuaian, Stok Dipinjam, Stok Sistem Akhir & Selisih.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Dari Tanggal</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="recon-start" /></div>
          <div><Label>Sampai Tanggal</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="recon-end" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={dl} disabled={busy} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="recon-download"><Download className="w-4 h-4 mr-1.5" />{busy ? "..." : "Download Excel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductCountDialog({ sid, product, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef({});
  useEffect(() => {
    api.get(`/opname/${sid}/product/${product.product_id}`).then((r) => {
      setData(r.data);
      const init = {};
      (r.data.variants || []).forEach((v) => v.warehouses.forEach((w) => { if (w.counted_qty != null) init[`${v.variant_id}_${w.warehouse_id}`] = String(w.counted_qty); }));
      setVals(init);
    }).catch(() => toast.error("Gagal memuat varian"));
  }, []);
  const setVal = (k, val) => setVals((s) => ({ ...s, [k]: val }));
  const multiWh = (data?.warehouses?.length || 0) > 1;
  const orderedKeys = data ? data.variants.flatMap((v) => v.warehouses.map((w) => `${v.variant_id}_${w.warehouse_id}`)) : [];
  const onKeyDown = (e, k) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const idx = orderedKeys.indexOf(k);
    const nk = orderedKeys[idx + 1];
    if (nk && inputRefs.current[nk]) { inputRefs.current[nk].focus(); inputRefs.current[nk].select?.(); }
    else saveAll();
  };
  const saveAll = async () => {
    const counts = [];
    (data.variants || []).forEach((v) => v.warehouses.forEach((w) => {
      const raw = vals[`${v.variant_id}_${w.warehouse_id}`];
      if (raw !== undefined && raw !== "") counts.push({ variant_id: v.variant_id, warehouse_id: w.warehouse_id, counted_qty: Number(raw) });
    }));
    if (!counts.length) return toast.error("Isi minimal 1 qty fisik");
    setSaving(true);
    try { const r = await api.post(`/opname/${sid}/count-bulk`, { counts }); toast.success(`${r.data.saved} varian tersimpan`); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="opname-product-dialog">
        <DialogHeader>
          <DialogTitle>{product.product_name}</DialogTitle>
          <DialogDescription>Isi qty fisik untuk semua varian sekaligus. Kosongkan varian yang tidak dihitung, lalu Simpan Semua. <span className="text-amber-700">Baris kuning = ada stok Keep</span> — hitung fisik termasuk barang keep (Fisik Seharusnya = Sistem + Keep).</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-wmuted">Memuat...</div> : (
          <div className="border border-taupe/20 rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Varian</TableHead>{multiWh && <TableHead>Gudang</TableHead>}<TableHead className="text-center">Sistem</TableHead><TableHead className="text-center">Keep</TableHead><TableHead className="text-center">Fisik Seharusnya</TableHead><TableHead className="text-center w-32">Qty Fisik</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.variants.length === 0 ? <TableRow><TableCell colSpan={multiWh ? 6 : 5} className="text-center py-8 text-wmuted">Produk ini belum punya varian</TableCell></TableRow>
                  : data.variants.flatMap((v) => v.warehouses.map((w) => {
                    const k = `${v.variant_id}_${w.warehouse_id}`;
                    return (
                      <TableRow key={k} data-testid={`opname-pv-${k}`} className={w.keep > 0 ? "bg-amber-50/70 hover:bg-amber-50" : ""}>
                        <TableCell className="text-sm">{v.color} {v.size}{w.counted_qty != null && <span className="ml-1.5 text-[11px] text-green-600" title="sudah dihitung">●</span>}</TableCell>
                        {multiWh && <TableCell className="text-sm">{w.warehouse_name}</TableCell>}
                        <TableCell className="text-center text-wmuted">{num(w.system_now)}</TableCell>
                        <TableCell className="text-center">{w.keep ? <span className="text-amber-600 font-medium" data-testid={`opname-pv-keep-${k}`}>{num(w.keep)}</span> : <span className="text-wmuted">0</span>}</TableCell>
                        <TableCell className="text-center font-semibold text-cocoa" data-testid={`opname-pv-expected-${k}`}>{num(w.expected_physical)}</TableCell>
                        <TableCell className="text-center"><Input ref={(el) => { inputRefs.current[k] = el; }} type="number" min={0} value={vals[k] ?? ""} onChange={(e) => setVal(k, e.target.value)} onKeyDown={(e) => onKeyDown(e, k)} className="h-8 w-24 mx-auto" placeholder="qty" data-testid={`opname-pv-input-${k}`} /></TableCell>
                      </TableRow>
                    );
                  }))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Tutup</Button>
          <Button onClick={saveAll} disabled={saving || !data} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="opname-pv-save-all">{saving ? "..." : "Simpan Semua"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
